import { History, MessageSquarePlus, Sparkles } from "lucide-react";
import { createElement } from "react";
import type { BootstrapPayload, Command } from "../../core";
import { fuzzyScore } from "../scoring";
import type { Candidate, CommandPaletteSource } from "../types";

type Dispatch = (cmd: Command) => Promise<unknown>;

interface ChatEntry {
    id: string;
    title: string;
    subtitle: string;
    keywords: string[];
    icon: () => ReturnType<typeof createElement>;
}

const CHAT_ENTRIES: ChatEntry[] = [
    {
        id: "open-assistant",
        title: "Open assistant",
        subtitle: "Talk to the AI assistant",
        keywords: ["ai", "assistant", "sidekick", "help"],
        icon: () => createElement(Sparkles, { size: 14 }),
    },
    {
        id: "new-conversation",
        title: "New conversation",
        subtitle: "Start a fresh chat thread",
        keywords: ["new", "chat", "thread", "message"],
        icon: () => createElement(MessageSquarePlus, { size: 14 }),
    },
    {
        id: "recent-chats",
        title: "Recent chats",
        subtitle: "Jump back into a recent conversation",
        keywords: ["recent", "history", "previous"],
        icon: () => createElement(History, { size: 14 }),
    },
];

/**
 * Chat source — surfaced via the "@" prefix. The chat / conversations backend
 * is not yet wired into the workspace runtime, so this source emits a small
 * set of placeholder entries (open assistant, start a new conversation, jump
 * to recent) and console.info on pick. The placement, ordering, and visual
 * shape match the canonical "@" mode so the swap-in when the real backend
 * arrives is a one-file change inside this source.
 *
 * Routing: declared with `prefix: "@"`. The engine strips the "@" and passes
 * the rest of the query to `search()`; an empty stripped query (user typed
 * just "@") shows all three entries via `fuzzyScore`'s empty-query rule.
 */
export function buildChatSource(_boot: BootstrapPayload, _dispatch: Dispatch): CommandPaletteSource {
    return {
        key: "chat",
        label: "Chat and AI",
        prefix: "@",
        priority: 50,
        search(query: string): Candidate[] {
            const out: Candidate[] = [];
            for (const entry of CHAT_ENTRIES) {
                const score = fuzzyScore(query, entry.title, entry.keywords);
                if (score <= 0) continue;
                out.push({
                    id: `chat:${entry.id}`,
                    sourceKey: "chat",
                    title: entry.title,
                    subtitle: entry.subtitle,
                    icon: entry.icon(),
                    keywords: entry.keywords,
                    score,
                    onPick: () => {
                        // Chat backend is not yet wired — log so the entry
                        // is visible but non-destructive. Replace with a
                        // proper command dispatch once the chat service
                        // lands.
                        console.info(`[command-palette] Chat action: ${entry.title} (no handler wired yet)`);
                    },
                });
            }
            return out;
        },
    };
}
